/**
 * SimpleLandingHeader
 *
 * Lightweight header used on simple/standalone landing pages. Provides back
 * navigation, optional templates link, enterprise logo support, and theme
 * toggle. Optional dropdown for destructive actions when `onDeleteClick` set.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Moon, Sun, Trash2, MoreVertical, X } from 'lucide-react';

export function SimpleLandingHeader({ 
  isDark, 
  toggleTheme, 
  backLink = "/", 
  demoTemplatesLink, 
  backButtonText = "Back to Home", 
  onBackClick,
  onNavigation,
  showDelete = false,
  onDeleteClick = null,
  isLoading = false,
  showLogo = true,
  enterpriseLogo = null,
  enterpriseName = null
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Only show dropdown if onDeleteClick is provided
  const showMoreOptions = !!onDeleteClick;
  
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Determine logo source
  const logoSrc = isDark ? "/images/logos/logo-white.png" : "/images/logos/logo-color.png";

  return (
    <header className="bg-white dark:bg-gray-900 pt-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Back/Action button */}
          <div className="w-1/3 flex justify-start">
            {onBackClick ? (
              <button 
                onClick={onBackClick}
                disabled={isLoading}
                className={`inline-flex items-center whitespace-nowrap text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 ${isLoading ? 'opacity-70 cursor-wait' : ''}`}
              >
                {isLoading ? (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700 dark:text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <ChevronLeft className="h-5 w-5 flex-shrink-0 mr-1" />
                )}
                <span className="text-sm font-medium">{isLoading ? "Signing out..." : backButtonText}</span>
              </button>
            ) : (
              <Link 
                to={backLink} 
                className="inline-flex items-center whitespace-nowrap text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400"
                onClick={onNavigation}
              >
                <ChevronLeft className="h-5 w-5 flex-shrink-0 mr-1" />
                <span className="text-sm font-medium">{backButtonText}</span>
              </Link>
            )}
          </div>
          
          {/* Centered logo - visible on all screen sizes */}
          <div className="w-1/3 flex justify-center">
            {(showLogo || enterpriseLogo) && (
              <div className="flex items-center">
                {enterpriseLogo ? (
                  <>
                    {/* Enterprise logo - only visible on md screens and up */}
                    <img 
                      src={enterpriseLogo} 
                      alt={`${enterpriseName} logo`}
                      className="hidden md:block h-6 w-auto max-w-full object-contain"
                      style={{ maxHeight: "24px" }}
                    />
                    {/* Advocacy Lab logo - visible on mobile (below md) */}
                    <Link to="/" className="md:hidden flex items-center" onClick={onNavigation}>
                      <img 
                        key={`header-logo-mobile-${isDark ? 'dark' : 'light'}`}
                        src={logoSrc}
                        alt="Advocacy Lab"
                        className="h-6 w-auto max-w-full object-contain"
                        style={{ maxHeight: "24px" }}
                      />
                    </Link>
                  </>
                ) : (
                  // Show Advocacy Lab logo
                  <Link to="/" className="flex items-center" onClick={onNavigation}>
                    <img 
                      key={`header-logo-${isDark ? 'dark' : 'light'}`}
                      src={logoSrc}
                      alt="Advocacy Lab"
                      className="h-6 w-auto max-w-full object-contain"
                      style={{ maxHeight: "24px" }}
                    />
                  </Link>
                )}
              </div>
            )}
          </div>
          
          {/* Right side actions: Templates Link (if provided), More Options & Dark mode toggle */}
          <div className="w-1/3 flex justify-end items-center space-x-4">
            {demoTemplatesLink && (
              <Link 
                to={demoTemplatesLink}
                className="text-sm font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400"
                onClick={onNavigation}
              >
                Templates
              </Link>
            )}

            {/* More Options Dropdown - Only if onDeleteClick is provided */}
            {showMoreOptions && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="rounded-lg p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                  aria-label="More options"
                >
                  {dropdownOpen ? (
                    <X className="w-5 h-5" />
                  ) : (
                    <MoreVertical className="w-5 h-5" />
                  )}
                </button>
                
                {/* Dropdown Menu */}
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                      {onDeleteClick && (
                        <button
                          onClick={() => {
                            setDropdownOpen(false);
                            onDeleteClick();
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete account
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <button
              onClick={toggleTheme}
              className="rounded-lg p-2 text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Toggle dark mode"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}